import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'antd';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { recentActivities, topPracticesByRevenue, trendData } from '../mockData';
import s from './s.module.less';

echarts.use([TooltipComponent, GridComponent, LegendComponent, LineChart, CanvasRenderer]);

const timeRanges = ['7d', '30d', '90d', '1y'];

function formatRevenue(value: number) {
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
}

function parseBoldText(text: string) {
    const parts = text.split(/\*\*(.*?)\*\*/);
    return parts.map((part, i) =>
        i % 2 === 1 ? (
            <span key={i} style={{ fontWeight: 600 }}>
                {part}
            </span>
        ) : (
            <span key={i}>{part}</span>
        ),
    );
}

const GPOverviewPage: React.FC = () => {
    const [activeRange, setActiveRange] = useState('30d');
    const [showAll, setShowAll] = useState(false);
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<echarts.ECharts | null>(null);

    const visibleActivities = showAll ? recentActivities : recentActivities.slice(0, 5);

    const chartData = useMemo(() => {
        const rangeMap: Record<string, number> = { '7d': 3, '30d': 9, '90d': 9, '1y': 9 };
        const count = rangeMap[activeRange] || 9;
        return trendData.slice(-count);
    }, [activeRange]);

    const renderChart = useCallback(() => {
        if (!chartRef.current) return;
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        const option: echarts.EChartsOption = {
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#fff',
                borderColor: '#E5E7EB',
                borderWidth: 1,
                textStyle: { fontSize: 12, color: '#333' },
                formatter: (params: any) => {
                    const items = params as any[];
                    let result = `<div style="font-weight:600;margin-bottom:4px">${items[0]?.axisValue}</div>`;
                    items.forEach((item: any) => {
                        const value =
                            item.seriesName === 'Revenue' ? `$${(item.value / 1000).toFixed(1)}K` : item.value;
                        result += `<div>${item.marker} ${item.seriesName}: ${value}</div>`;
                    });
                    return result;
                },
            },
            grid: { top: 20, right: 50, left: 50, bottom: 30 },
            xAxis: {
                type: 'category',
                data: chartData.map((d) => d.date),
                axisLine: { lineStyle: { color: '#E5E7EB' } },
                axisTick: { show: false },
                axisLabel: { fontSize: 11, color: '#9CA3AF' },
            },
            yAxis: [
                {
                    type: 'value',
                    position: 'left',
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } },
                    axisLabel: { fontSize: 11, color: '#156938', formatter: (v: number) => formatRevenue(v) },
                    min: 0,
                    max: 300000,
                },
                {
                    type: 'value',
                    position: 'right',
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    axisLabel: { fontSize: 11, color: '#9CA3AF' },
                    min: 0,
                    max: 50,
                },
            ],
            series: [
                {
                    name: 'Revenue',
                    type: 'line',
                    yAxisIndex: 0,
                    data: chartData.map((d) => d.revenue),
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: { color: '#156938', width: 2 },
                    itemStyle: { color: '#156938' },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: 'rgba(21, 105, 56, 0.12)' },
                            { offset: 1, color: 'rgba(21, 105, 56, 0.01)' },
                        ]),
                    },
                },
                {
                    name: 'Active practices',
                    type: 'line',
                    yAxisIndex: 1,
                    data: chartData.map((d) => d.practices),
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: { color: '#888', width: 1.5, type: 'dashed' },
                    itemStyle: { color: '#888' },
                },
            ],
        };

        chartInstance.current.setOption(option, true);
    }, [chartData]);

    useEffect(() => {
        renderChart();
        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [renderChart]);

    return (
        <div className={s.wrap}>
            <h1 className={s.pageTitle}>Group Practice Overview</h1>

            <div className={s.kpiRow}>
                <Card className={s.kpiCard}>
                    <div className={s.kpiTitle}>Active practices</div>
                    <div className={s.kpiValue}>42</div>
                    <div className={s.kpiSub} style={{ color: '#1F7A3D' }}>
                        +3 this month
                    </div>
                </Card>
                <Card className={s.kpiCard}>
                    <div className={s.kpiTitle}>Pending review</div>
                    <div className={s.kpiValue}>5</div>
                    <div className={s.kpiSub}>
                        <a style={{ color: '#156938', fontSize: 13, textDecoration: 'underline' }}>Review now</a>
                    </div>
                </Card>
                <Card className={s.kpiCard}>
                    <div className={s.kpiTitle}>Needs attention</div>
                    <div className={s.kpiValue}>3</div>
                    <div className={s.kpiSub} style={{ color: '#6b7280' }}>
                        Practices flagged
                    </div>
                </Card>
                <Card className={s.kpiCard}>
                    <div className={s.kpiTitle}>Revenue (30d)</div>
                    <div className={s.kpiValue}>$245K</div>
                    <div className={s.kpiSub} style={{ color: '#1F7A3D' }}>
                        +12% vs last month
                    </div>
                </Card>
            </div>

            <Card className={s.chartCard}>
                <div className={s.chartHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <span className={s.chartTitle}>GP business trend</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: '#6b7280' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#156938',
                                        display: 'inline-block',
                                    }}
                                />
                                Revenue
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#9CA3AF',
                                        display: 'inline-block',
                                    }}
                                />
                                Active practices
                            </span>
                        </span>
                    </div>
                    <div className={s.rangeBtns}>
                        {timeRanges.map((range) => (
                            <button
                                key={range}
                                className={`${s.rangeBtn} ${activeRange === range ? s.rangeBtnActive : ''}`}
                                onClick={() => setActiveRange(range)}
                            >
                                {range}
                            </button>
                        ))}
                    </div>
                </div>
                <div ref={chartRef} style={{ width: '100%', height: 230 }} />
            </Card>

            <div className={s.bottomRow}>
                <Card className={s.activityCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className={s.sectionTitle}>Recent activity</span>
                        <span className={s.showMore} onClick={() => setShowAll(!showAll)}>
                            {showAll ? 'Show less' : 'Show more'}
                        </span>
                    </div>
                    <div className={s.activityList}>
                        {visibleActivities.map((event) => (
                            <div key={event.id} className={s.activityItem}>
                                <span className={s.activityIcon}>{event.icon}</span>
                                <div className={s.activityContent}>
                                    <div
                                        className={s.activityMain}
                                        style={event.icon === '⚠️' ? { color: '#B85714' } : undefined}
                                    >
                                        {parseBoldText(event.mainText)}
                                    </div>
                                    <div className={s.activitySecondary}>{event.secondaryText}</div>
                                </div>
                                <span className={s.activityTime}>{event.time}</span>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card className={s.topCard}>
                    <span className={s.sectionTitle}>Top practices by revenue (30d)</span>
                    <div className={s.topList}>
                        {topPracticesByRevenue.map((p, i) => (
                            <div key={p.name} className={s.topItem}>
                                <span className={s.topRank}>{i + 1}</span>
                                <span className={s.topName}>{p.name}</span>
                                <span className={s.topRevenue}>{p.revenue}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default GPOverviewPage;
